import { useId, type ImgHTMLAttributes, type SVGProps } from 'react'

import { cn } from '@/lib/cn'

type IconProps = SVGProps<SVGSVGElement>
type BrandIconProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'>

export function EyeIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}

export function EyeOffIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M2.5 12S6 5.5 12 5.5c1.6 0 3 .47 4.2 1.15M21.5 12s-1.2 2.23-3.4 4.03M9.9 9.9a3 3 0 1 0 4.24 4.24"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.1 16.03A10.2 10.2 0 0 1 12 18.5C6 18.5 2.5 12 2.5 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="m4 4 16 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="m9 5 7 7-7 7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function ArrowLeftIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M15 5 8 12l7 7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Indonesian flag, drawn rather than fetched so it works offline. */
export function FlagIdIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 16" aria-hidden="true" {...props}>
      <rect width="24" height="8" fill="#ce1126" />
      <rect y="8" width="24" height="8" fill="#fff" />
    </svg>
  )
}

/** Rounded Indonesian flag mark for unpaid-order buyer fields. */
export function FlagInIcon({ className, ...props }: BrandIconProps) {
  return (
    <img
      src="/assets/icons/flag_in.png"
      alt=""
      aria-hidden
      className={cn('size-8 shrink-0 object-contain', className)}
      {...props}
    />
  )
}

export function GoogleIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        fill="#4285F4"
        d="M23.5 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.45a5.5 5.5 0 0 1-2.39 3.61v3h3.86c2.26-2.08 3.58-5.15 3.58-8.8Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.87-3a7.2 7.2 0 0 1-10.72-3.78H1.4v3.09A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.36 14.3a7.12 7.12 0 0 1 0-4.59V6.62H1.4a12 12 0 0 0 0 10.77l3.96-3.09Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.43-3.42C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.4 6.62l3.96 3.09A7.15 7.15 0 0 1 12 4.75Z"
      />
    </svg>
  )
}

export function AppleIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M17.05 12.72c-.02-2.1 1.72-3.11 1.8-3.16-.98-1.44-2.5-1.63-3.05-1.65-1.3-.13-2.53.76-3.19.76-.66 0-1.67-.74-2.75-.72-1.42.02-2.72.82-3.45 2.09-1.47 2.55-.38 6.33 1.06 8.4.7 1.01 1.54 2.15 2.64 2.11 1.06-.04 1.46-.68 2.74-.68s1.64.68 2.75.66c1.14-.02 1.86-1.03 2.56-2.05.8-1.17 1.13-2.31 1.15-2.37-.03-.01-2.2-.85-2.22-3.36ZM14.96 5.6c.58-.71.98-1.7.87-2.68-.84.03-1.86.56-2.46 1.27-.54.62-1.01 1.62-.88 2.58.94.07 1.9-.47 2.47-1.17Z" />
    </svg>
  )
}

export function ScanIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="7" y="7" width="10" height="10" rx="1" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
}

export function LockIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M8 11V7a4 4 0 1 1 8 0v4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function ChevronUpIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="m5 15 7-7 7 7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function MinusIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path d="M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

export function UserIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M5.2 19c.8-3 3.4-4.6 6.8-4.6s6 1.6 6.8 4.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Colored person sticker for the buyer name field (Figma Jastiper). */
export function BuyerPersonIcon(props: IconProps) {
  const clipId = `buyer-person-${useId().replace(/:/g, '')}`

  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" {...props}>
      <rect x="3" y="3" width="15.2" height="15.2" rx="3.4" fill="#E4E7EC" />
      <g clipPath={`url(#${clipId})`}>
        <rect x="1.4" y="1.4" width="15.2" height="15.2" rx="3.4" fill="#fff" />
        <circle cx="9" cy="7.55" r="2.55" fill="#F2922F" stroke="#2B3034" strokeWidth="1.25" />
        <ellipse cx="9" cy="17.1" rx="5.35" ry="4.7" fill="#EE3B2B" stroke="#2B3034" strokeWidth="1.25" />
      </g>
      <rect
        x="1.4"
        y="1.4"
        width="15.2"
        height="15.2"
        rx="3.4"
        stroke="#2B3034"
        strokeWidth="1.45"
      />
      <defs>
        <clipPath id={clipId}>
          <rect x="1.4" y="1.4" width="15.2" height="15.2" rx="3.4" />
        </clipPath>
      </defs>
    </svg>
  )
}

export function ChatBubbleIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v7A2.5 2.5 0 0 1 16.5 16H11l-4 3.2V16H7.5A2.5 2.5 0 0 1 5 13.5v-7Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function WhatsAppIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12.04 2.5A9.5 9.5 0 0 0 4.2 16.7L3 21l4.4-1.15A9.5 9.5 0 1 0 12.04 2.5Zm5.5 13.42c-.23.65-1.35 1.24-1.88 1.32-.48.07-1.1.1-1.78-.11-.41-.13-.94-.31-1.62-.6-2.85-1.23-4.7-4.1-4.84-4.29-.14-.19-1.15-1.53-1.15-2.92 0-1.39.73-2.07.99-2.35.26-.28.57-.35.76-.35h.55c.17 0 .41-.07.64.49.23.57.78 1.96.85 2.1.07.14.12.31.02.5-.1.19-.14.31-.28.48-.14.17-.3.38-.43.51-.14.14-.29.29-.12.56.17.28.75 1.24 1.61 2.01 1.11.99 2.05 1.3 2.33 1.44.28.14.44.12.6-.07.16-.19.7-.81.88-1.09.19-.28.37-.23.62-.14.26.09 1.63.77 1.91.91.28.14.47.21.54.33.07.12.07.69-.16 1.34Z" />
    </svg>
  )
}

/** Brand WhatsApp mark for inline “Chat” links in order tables. */
export function WhatsAppBrandIcon({ className, ...props }: BrandIconProps) {
  return (
    <img
      src="/assets/icons/whatsapp.png"
      alt=""
      aria-hidden
      className={cn('size-3.5 shrink-0 object-contain', className)}
      {...props}
    />
  )
}

/** Riwayat chat per item pesanan (Belum bayar). */
export function ItemChatIcon({ className, ...props }: BrandIconProps) {
  return (
    <img
      src="/assets/icons/chat-history.png"
      alt=""
      aria-hidden
      className={cn('size-8 shrink-0 object-contain', className)}
      {...props}
    />
  )
}

/** Item chat bubble for undetected order line items. */
export function ItemChatErrorIcon({ className, ...props }: BrandIconProps) {
  return (
    <img
      src="/assets/icons/item-chat-error.png"
      alt=""
      aria-hidden
      className={cn('size-4 shrink-0 object-contain', className)}
      {...props}
    />
  )
}

/** Buyer avatar for detected buyer name fields. */
export function BuyerAvatarIcon({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      className={cn('size-8 shrink-0', className)}
      {...props}
    >
      <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="7" fill="#fff" stroke="#E4E7EC" strokeWidth="1" />
      <circle cx="16" cy="11.5" r="4.5" fill="#F4B942" stroke="#374151" strokeWidth="1" />
      <path d="M9.5 22.5a6.5 6.5 0 0 1 13 0" fill="#EE3B2B" />
    </svg>
  )
}

/** Buyer avatar when receiver name is missing from the order. */
export function BuyerAvatarErrorIcon({ className, ...props }: BrandIconProps) {
  return (
    <img
      src="/assets/icons/buyer-avatar-error.png"
      alt=""
      aria-hidden
      className={cn('size-8 shrink-0 object-contain', className)}
      {...props}
    />
  )
}

/** Sender address mark for shipping detail panel. */
export function SenderAddressIcon({ className, ...props }: BrandIconProps) {
  return (
    <img
      src="/assets/icons/sender-address.png"
      alt=""
      aria-hidden
      className={cn('size-6 shrink-0 object-contain', className)}
      {...props}
    />
  )
}

/** Store info mark for shipping detail panel. */
export function SenderStoreIcon({ className, ...props }: BrandIconProps) {
  return (
    <img
      src="/assets/icons/sender-store.png"
      alt=""
      aria-hidden
      className={cn('size-6 shrink-0 object-contain', className)}
      {...props}
    />
  )
}

/** Red plus mark for store logo upload dropzone. */
export function UploadPlusIcon({ className, ...props }: BrandIconProps) {
  return (
    <img
      src="/assets/icons/upload-plus.png"
      alt=""
      aria-hidden
      className={cn('size-11 shrink-0 object-contain', className)}
      {...props}
    />
  )
}

/** Brand cart mark for Daftar Belanja actions. */
export function CartBrandIcon({ className, ...props }: BrandIconProps) {
  return (
    <img
      src="/assets/icons/cart.png"
      alt=""
      aria-hidden
      className={cn('size-5 shrink-0 object-contain', className)}
      {...props}
    />
  )
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="m5 9 7 7 7-7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function PlusIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

export function CloseIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="m6 6 12 12M18 6 6 18"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Filled pink circle with a red X (Figma 2121:51590). */
export function FieldClearIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="12" fill="#F9E9E9" />
      <path
        d="m8 8 8 8M16 8l-8 8"
        stroke="#EE3B2B"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function CopyIcon({ className, strokeWidth = 1.6, ...props }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className} {...props}>
      <rect x="8" y="8" width="11" height="11" rx="2" stroke="currentColor" strokeWidth={strokeWidth} />
      <path
        d="M5 16V6a2 2 0 0 1 2-2h10"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Two stacked squares — compact copy affordance in order tables (WEBLP-2236). */
export function CopyStackIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" {...props}>
      <rect x="5.25" y="2.75" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.25" />
      <rect x="2.75" y="5.25" width="8" height="8" rx="1.5" fill="currentColor" />
    </svg>
  )
}

export function CopyBoldIcon({ className, ...props }: IconProps) {
  return (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true" className={className} {...props}>
      <path
        d="M12.5 4.5H5.83333C5.09695 4.5 4.5 5.09695 4.5 5.83333V12.5C4.5 13.2364 5.09695 13.8333 5.83333 13.8333H12.5C13.2364 13.8333 13.8333 13.2364 13.8333 12.5V5.83333C13.8333 5.09695 13.2364 4.5 12.5 4.5Z"
        fill="currentColor"
      />
      <path
        d="M9.33333 1.83333C9.33333 1.37614 8.95719 1 8.5 1H1.83333C1.37614 1 1 1.37614 1 1.83333V8.5C1 8.95719 1.37614 9.33333 1.83333 9.33333C2.10948 9.33333 2.33333 9.55719 2.33333 9.83333C2.33333 10.1095 2.10948 10.3333 1.83333 10.3333C0.823858 10.3333 0 9.50948 0 8.5V1.83333C8.64811e-08 0.823858 0.823858 8.64797e-08 1.83333 0H8.5C9.50948 0 10.3333 0.823858 10.3333 1.83333C10.3333 2.10948 10.1095 2.33333 9.83333 2.33333C9.55719 2.33333 9.33333 2.10948 9.33333 1.83333Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function SearchIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

export function CartIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M3 4h2.2l1.9 10.2a1.6 1.6 0 0 0 1.6 1.3h7.9a1.6 1.6 0 0 0 1.6-1.2L20 7.5H6.3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9.5" cy="19" r="1.4" fill="currentColor" />
      <circle cx="16.5" cy="19" r="1.4" fill="currentColor" />
    </svg>
  )
}

export function AlertCircleIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="9" fill="currentColor" />
      <path d="M12 7.5v5.2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="16.2" r="1.05" fill="#fff" />
    </svg>
  )
}

/** Blue info badge for informational callouts. */
export function InfoCircleIcon({
  tone = 'brand',
  ...props
}: IconProps & { tone?: 'brand' | 'ink' }) {
  if (tone === 'ink') {
    return (
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" {...props}>
        <circle cx="8" cy="8" r="8" fill="currentColor" />
        <path d="M8 7v4" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="8" cy="5" r="0.85" fill="#fff" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" {...props}>
      <circle cx="8" cy="8" r="8" fill="#0095DA" />
      <path d="M8 7v4" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="5" r="0.85" fill="#fff" />
    </svg>
  )
}

export function UploadIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 15.5V4.5m0 0L8 8.5m4-4 4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.5 14.5v3a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function ClipboardCheckIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M9 4.5H7a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-11a2 2 0 0 0-2-2h-2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <rect x="9" y="3" width="6" height="3.2" rx="1.1" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="m9 13 2 2 4-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function SendIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M20 4 3.8 10.6a.5.5 0 0 0 .05.94L10 13.2l1.7 6.1a.5.5 0 0 0 .93.1L20 4Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M20 4l-10 9.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

export function ClockIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12 7.4V12l3 1.9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function BellIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M6.5 10a5.5 5.5 0 0 1 11 0c0 3 .7 4.6 1.4 5.5.4.5 0 1.2-.6 1.2H5.7c-.6 0-1-.7-.6-1.2.7-.9 1.4-2.5 1.4-5.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M10 19.5a2.2 2.2 0 0 0 4 0"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function RefreshIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M19.5 12a7.5 7.5 0 1 1-2.6-5.7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M19.7 4.5v4h-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function CheckIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M20 6L9 17L4 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function PrinterIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M7 8V4h10v4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <rect x="5" y="8" width="14" height="8" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 16h10v4H7v-4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="17" cy="11" r="1" fill="currentColor" />
    </svg>
  )
}

export function ShippingLabelIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M4 8.5 12 4l8 4.5V18a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M12 4v15" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M8.5 12h7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function TrackIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M4 18h16"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M7 18V9l5-3 5 3v9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M10 12h4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function PackageIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 3 4 7v10l8 4 8-4V7l-8-4Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M12 12 4 7M12 12l8-5M12 12v10" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}

export function ChartBarIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M6 18V12M10 18V8M14 18V14M18 18V6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Map pin: tip sits on the point; size the outer box, not the path. */
export function MapPinIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 40 52" fill="none" aria-hidden="true" {...props}>
      <path
        d="M20 51s16-16.4 16-31A16 16 0 1 0 4 20c0 14.6 16 31 16 31Z"
        fill="currentColor"
      />
      <circle cx="20" cy="20" r="6.5" fill="white" />
    </svg>
  )
}

/** Modal illustration: sender address map pin with completion badge. */
export function ShippingAddressPromptIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 120 120" fill="none" aria-hidden="true" {...props}>
      <ellipse cx="60" cy="108" rx="22" ry="5" fill="#E4E7EC" />
      <path
        d="M60 98c16.568-17.2 28-29.4 28-44A28 28 0 1 0 32 54c0 14.6 11.432 26.8 28 44Z"
        fill="#8C9196"
        stroke="#2B3034"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="60" cy="54" r="18" fill="#F2922F" />
      <path
        d="M49 54.5 57 62.5 73 46.5"
        stroke="#EE3B2B"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function MyLocationIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2v3M12 19v3M2 12h3M19 12h3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function StoreIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M4 10 6 4h12l2 6M4 10h16v10H4V10Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}

export function LinkIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M10 14a3.5 3.5 0 0 0 4.95 0l2.12-2.12a3.5 3.5 0 1 0-4.95-4.95L11 8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M14 10a3.5 3.5 0 0 0-4.95 0L6.93 12.12a3.5 3.5 0 1 0 4.95 4.95L13 16"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function PencilIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="m14 5 5 5M4 20h4.5L20 8.5l-4.5-4.5L4 15.5V20Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function TrashIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M4 7h16M10 7V5h4v2M7 7l1 14h8l1-14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
